import React from 'react';
import { useRouter } from 'next/router';
import Skeleton from '@mui/material/Skeleton';
import { CardHcd } from '@maiia/model/generated/model/api-patient/api-patient';
import { useSelector } from 'react-redux';
import { Button, NoSsr, Typography } from '@mui/material';
import { css, styled } from '@mui/material/styles';
import Link from '../../atoms/Link/Link';
import { useTranslation } from '@/src/i18n';

import Tag from '../../atoms/Tag/Tag';
import {
  getSpecialityName,
  getPostalCodeAndCity,
  getSector,
  getNumberAndStreetName,
  getCardExpertiseTag,
  getCardSpecialityTag,
} from '../../utils/card';
import { getCardFullName, getSubstitute } from '../../utils';
import { LIMITATION_CENTER } from '../../molecules/FilterBar/TLCLimitationField';
import FlagPractitioner from '../../molecules/FlagPractitioner/FlagPractitioner';
import Text from '../../atoms/Text/Text';
import { useMediaQueryBreakPoint, IS_PHONE_OR_TABLET } from '@/src/hooks';
import FlagCenter from '../../molecules/FlagCenter/FlagCenter';
import { ListMode } from '../SearchResults/types';

/* JS styling */

const PratName = styled('a')(
  ({ theme }) => css`
    color: ${theme.palette.primary.main};
    position: relative;
    svg.care-status {
      display: inline;
    }
    .care-status {
      position: absolute;
      top: 0;
      right: 0;
      &.primary,
      &.favorite {
        height: ${theme.spacing(2)};
        width: ${theme.spacing(2)};
      }
    }
    &:hover {
      text-decoration: underline;
    }
  `,
);
const FullNamePractitioner = styled(Typography)(
  ({ theme }) => css`
    word-break: break-word;
    padding-right: ${theme.spacing(3)};
    font-size: ${theme.typography.h6.fontSize};
    font-weight: 600;
    ${theme.breakpoints.up('sm')} {
      font-size: ${theme.typography.button.fontSize};
      font-weight: ${theme.typography.button.fontWeight};
    }
  `,
) as typeof Typography;

interface Props {
  item: CardHcd;
  mode?: ListMode;
  onClick: React.ReactEventHandler;
  hasAvailabilities: boolean;
  isLoading: boolean;
}

const SearchCardBody: React.FC<Props> = ({
  item,
  mode,
  onClick,
  hasAvailabilities,
  isLoading,
}) => {
  const { t } = useTranslation();
  const { pathname } = useRouter();
  const currentSearch = useSelector((state: any) => state?.suggestions?.item);
  const postalCodeAndCity = getPostalCodeAndCity(item);
  const expertiseTag = getCardExpertiseTag(item, currentSearch);
  const specialityTag = getCardSpecialityTag(item, currentSearch);
  const isTlc = !!mode;

  const isAvailableForConsultation =
    hasAvailabilities ||
    item?.settings?.firstPhysicalFreeTimeslot ||
    item?.settings?.firstTeleconsultationFreeTimeslot;

  const isCenter = item?.type === 'CENTER';
  // TODO(npham): Refactor this
  const allowTakeAppointment =
    !isTlc &&
    item?.settings?.clientDocavenue &&
    (item?.consultationReasons ?? []).length > 0 &&
    isAvailableForConsultation &&
    // Check if he can receive appointments online (for prats)...
    (item?.settings?.isOnlineAppointment ||
      // ... or if he can take TLC appointments ...
      item?.settings?.isTeleconsultation ||
      // ... or in case of a center, it doesn't have online appointments.
      // We must then show the button only if there are availabilities, which is
      // checked inside isAvailableForConsultation (a few lines above).
      isCenter);

  const isPhoneOrTablet: boolean = useMediaQueryBreakPoint(IS_PHONE_OR_TABLET);
  const { isSubstitute: hasSubstituteData, substitute } = getSubstitute(item);
  const isSubstitute =
    hasSubstituteData && pathname.includes('teleconsultation');
  const fullNamePractitioner = isSubstitute
    ? `${substitute?.firstName} ${substitute?.lastName}`
    : getCardFullName(t, item);

  const renderPratName = () => {
    const pratName = (
      <PratName
        data-cy="practitioner-name"
        title={fullNamePractitioner}
        aria-label={fullNamePractitioner}
        href={isTlc && isPhoneOrTablet ? '#' : '/'}
        onClick={e => {
          (window as any).dataLayer?.push({
            event: 'SearchCardBody_clickOnCardTitle',
            center: item?.center,
            professional: item?.professional,
            type: item?.type,
          });
          // Set property to event for tell parent dont track again
          // We dont use stopPropagation because we need the parent handle this event too
          (e as any).tracked = true;
        }}
      >
        <FullNamePractitioner component="h2">
          {fullNamePractitioner}
        </FullNamePractitioner>
        {item?.professional?.practitionerId ? (
          <FlagPractitioner
            practitionerId={item?.professional?.practitionerId}
          />
        ) : (
          <FlagCenter centerId={item?.center?.id} />
        )}
      </PratName>
    );
    if (!item?.url || (isTlc && isPhoneOrTablet)) return pratName;
    return (
      <Link
        href={{
          pathname: item.url,
          query: {
            ...(item?.center?.id ? { centerId: item.center.id } : {}),
            ...(pathname.includes('teleconsultation') && {
              fromImmediateTlc: true,
            }),
          },
        }}
        passHref
        legacyBehavior
      >
        {pratName}
      </Link>
    );
  };

  return (
    <div className="search-card__infos">
      {renderPratName()}
      {isSubstitute && (
        <Text className="mention-sub">
          {t('substitute_for', {
            firstname: item.professional?.firstName,
            lastname: item.professional?.lastName,
          })}
        </Text>
      )}
      <Text className="prat__spe" component="h3">
        {getSpecialityName(item)}
      </Text>
      <Text className="prat__address" variant="paragraph" component="p">
        {getNumberAndStreetName(item)}
        <br />
        {postalCodeAndCity.replace(',', '')}
      </Text>
      <Text
        className="prat__zipcity killondesk"
        variant="paragraph"
        component="p"
      >
        {postalCodeAndCity}
      </Text>
      <Text className="prat__secu" variant="paragraph" component="p">
        {item?.publicInformation?.conventionSectors?.map(sector => (
          <Text key={sector}>{t(getSector(sector))}</Text>
        ))}
      </Text>
      {mode === 'tlc' &&
        item?.settings?.teleconsultationPatientLimitation ===
          LIMITATION_CENTER && (
          <Text className="warning" variant="paragraph" component="p">
            {t(`tlc_limitation_center_warning`)}
          </Text>
        )}

      {!isTlc && (
        <>
          {expertiseTag && <Tag>{expertiseTag}</Tag>}
          {specialityTag && <Tag>{specialityTag}</Tag>}
        </>
      )}

      {allowTakeAppointment && (
        <div className="prat__rdv-button killonmobile">
          <NoSsr>
            {/* Disable skeleton on tablet as we show the button but not the availabilities, otherwise it's loading
              forever (onAvailabilitiesSuccess never called) */}
            {isLoading && !isPhoneOrTablet ? (
              <Skeleton width={190} height={30} />
            ) : (
              <Button
                sx={{ px: 6 }}
                variant="contained"
                color="secondary"
                onClick={onClick}
                data-cy="btn-searchcard-get-rdv"
              >
                {t('get-appointment')}
              </Button>
            )}
          </NoSsr>
        </div>
      )}
    </div>
  );
};

export default SearchCardBody;
